"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Clock, Calendar, FileText, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface StaticArticleFallback {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  readTime: string;
  date: string;
  link: string;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
}
const fallbackArticles: StaticArticleFallback[] = [{
  id: "guide-1",
  title: "Navigating Brookhaven building permits and historic zoning regulations",
  excerpt: "Learn setback requirements, tree canopy preservation rules, and how to expedite DeKalb County permit approvals for your renovation.",
  category: "Permits & Zoning",
  readTime: "5 min read",
  date: "March 12, 2025",
  link: "/ContactRequestAnEstimatePage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_architectural_plans.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_architectural_plans.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_architectural_plans.webp-wvc-sizes",
    alt: "Architectural blueprints and permit documents for Brookhaven residence"
  }
}, {
  id: "guide-2",
  title: "Kitchen renovation costs in North Atlanta: 2025 realistic budget breakdown",
  excerpt: "Understand custom cabinetry allowances, quartzite slab pricing, and MEP trade costs before initiating your design phase.",
  category: "Cost Breakdown",
  readTime: "7 min read",
  date: "February 28, 2025",
  link: "/KitchenRemodelingPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_kitchen_custom_millwork.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_kitchen_custom_millwork.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_kitchen_custom_millwork.png-wvc-sizes",
    alt: "Renovated kitchen featuring custom rift-sawn oak cabinetry and marble island"
  }
}, {
  id: "guide-3",
  title: "Second-story additions vs. main-level expansions: what Brookhaven lots allow",
  excerpt: "A realistic comparison of foundation reinforcement costs, roof tie-in logistics, and municipal height restrictions.",
  category: "Addition Planning",
  readTime: "6 min read",
  date: "January 19, 2025",
  link: "/AdditionsBasementsOutdoorLivingPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_framing_exterior.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_framing_exterior.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_framing_exterior.png-wvc-sizes",
    alt: "Residential home addition framing and structural integration"
  }
}];
function ArticlesConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[0, 1, 2].map((_, i) => <div key={i} data-index={i} className="flex flex-col rounded-xl border border-border/60 bg-card overflow-hidden animate-pulse">
            <div className="aspect-[3/2] w-full bg-muted" />
            <div className="p-6 flex flex-col gap-4">
              <div className="h-5 w-24 rounded bg-muted" />
              <div className="h-7 w-5/6 rounded bg-muted" />
              <div className="space-y-2 pt-2">
                <div className="h-4 w-full rounded bg-muted" />
                <div className="h-4 w-4/5 rounded bg-muted" />
              </div>
              <div className="pt-6 border-t border-border/40 flex justify-between">
                <div className="h-4 w-20 rounded bg-muted" />
                <div className="h-4 w-24 rounded bg-muted" />
              </div>
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-8 text-center">
        <p className="text-foreground text-sm font-medium">Unable to load remodeling publications at this time.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-4">
          Try again
        </Button>
      </div>;
  }

  // Fallback to static editorial guides if posts provider returns empty
  const displayItems = posts && posts.length > 0 ? posts : null;
  if (!displayItems) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {fallbackArticles.map((article, i) => <article key={article.id} data-index={i} className="group flex flex-col rounded-xl border border-border/60 bg-card overflow-hidden shadow-xs hover:shadow-md transition-[box-shadow,border-color,transform] duration-250 hover:-translate-y-1 hover:border-primary/40">
            <div className="relative aspect-[3/2] w-full overflow-hidden bg-muted">
              <img src={article.image.src} data-wvc-srcset={article.image.srcset} data-wvc-sizes={article.image.sizes} alt={article.image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" />
              <div className="absolute inset-0 ring-1 ring-inset ring-black/5 pointer-events-none" />
              <div className="absolute top-3 left-3">
                <span className="inline-flex items-center rounded-md bg-secondary px-2.5 py-1 text-xs font-medium text-secondary-foreground shadow-xs">
                  {article.category}
                </span>
              </div>
            </div>

            <div className="flex flex-1 flex-col justify-between p-6">
              <div className="space-y-3">
                <h3 className="font-serif text-xl font-semibold leading-snug text-card-foreground group-hover:text-primary transition-colors">
                  <Link to={article.link} className="hover:underline">
                    {article.title}
                  </Link>
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {article.excerpt}
                </p>
              </div>

              <div className="pt-6 mt-6 border-t border-border/60 flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1">
                    <Clock className="size-3.5 text-muted-foreground" />
                    {article.readTime}
                  </span>
                  <span>•</span>
                  <span>{article.date}</span>
                </div>

                <Link to={article.link} className="inline-flex items-center gap-1 font-medium text-foreground group-hover:text-primary transition-colors">
                  <span>Read guide</span>
                  <ArrowRight className="size-3 text-primary transition-transform duration-200 group-hover:translate-x-0.5" />
                </Link>
              </div>
            </div>
          </article>)}
      </div>;
  }
  return <div className="space-y-12">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {displayItems.map((post, i) => {
        const categoryName = post.categories && post.categories.length > 0 ? post.categories[0].name : "Renovation Guide";
        const articleUrl = post.link || "/BlogPage";
        return <Card key={post.id} data-index={i} className="group flex flex-col overflow-hidden rounded-xl border border-border/60 bg-card p-0 shadow-xs hover:shadow-md transition-[box-shadow,border-color,transform] duration-250 hover:-translate-y-1 hover:border-primary/40">
              <div className="relative aspect-[3/2] w-full overflow-hidden bg-muted">
                {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-muted/40">
                    <FileText className="size-10 text-muted-foreground/40" />
                  </div>}
                <div className="absolute inset-0 ring-1 ring-inset ring-black/5 pointer-events-none" />
                <div className="absolute top-3 left-3">
                  <Badge variant="secondary" className="px-2.5 py-1 text-xs">
                    {categoryName}
                  </Badge>
                </div>
              </div>

              <CardContent className="flex flex-1 flex-col justify-between p-6">
                <div className="space-y-3">
                  <h3 className="font-serif text-xl font-semibold leading-snug text-card-foreground group-hover:text-primary transition-colors">
                    <Link to={articleUrl} className="hover:underline">
                      {post.title}
                    </Link>
                  </h3>
                  {post.excerpt && <div className="text-sm leading-relaxed text-muted-foreground line-clamp-3" dangerouslySetInnerHTML={{
                __html: post.excerpt
              }} />}
                </div>
              </CardContent>

              <CardFooter className="px-6 pb-6 pt-0 mt-auto border-t border-border/60 pt-4 flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center gap-2">
                  <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                  <span>
                    {post.date ? new Date(post.date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric"
                }) : "Recent"}
                  </span>
                </div>

                <Link to={articleUrl} className="inline-flex items-center gap-1 font-medium text-foreground group-hover:text-primary transition-colors">
                  <span>Read guide</span>
                  <ArrowRight className="size-3 text-primary transition-transform duration-200 group-hover:translate-x-0.5" />
                </Link>
              </CardFooter>
            </Card>;
      })}
      </div>

      {(hasNext || hasPrev) && <div className="flex items-center justify-center gap-4 pt-6">
          <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrev} className="flex items-center gap-1.5">
            <ChevronLeft className="size-4" />
            Previous
          </Button>

          <span className="text-xs text-muted-foreground px-2">
            Page {currentPage} of {totalPages || 1}
          </span>

          <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNext} className="flex items-center gap-1.5">
            Next
            <ChevronRight className="size-4" />
          </Button>
        </div>}
    </div>;
}
export default function Articles() {
  const wp_query_params = {
    post_type: "post",
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="articles-list" data-nav="light" className="bg-background py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="mx-auto max-w-3xl text-center mb-16 space-y-4">
          <div className="inline-flex items-center rounded-full border border-primary/35 bg-primary/10 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-widest text-primary">
            Remodeling guidance and insights
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-tight">
            Architectural planning and renovation guides for Brookhaven
          </h2>

          <div className="mx-auto h-[1px] w-20 bg-primary/40 my-2" />

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Practical cost breakdowns, local zoning insights, permitting checklists, and addition timelines designed for Brookhaven and North Atlanta homeowners.
          </p>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <ArticlesConsumer />
        </PostsProvider>
      </div>
    </section>;
}